/**
 * PageShell — 所有業務頁面的通用外殼（v3 "Quiet Refinement"）
 * ──────────────────────────────────────────────
 * 提供統一的 page-header 排版：
 *   - title（Onest display 字體大標，weight 600）
 *   - description（副標題，stone-500 弱化）
 *   - meta（狀態 chip / ID / 時間等並排展示，灰色分隔點）
 *   - actions（右上按鈕組）
 *   - banner（標題下方的全寬通知條，可選）
 *
 * 設計原則：
 *   - Header 底部 1px border（var(--border-subtle)），比「卡片式」更輕
 *   - 不引入額外容器，children 自由排版
 *   - 排版用 flex，響應式自動換行
 *   - 頁面 padding：`48px 0 32px 0`（頂部更寬鬆）
 */
import type { ReactNode } from 'react';
import { color, space, text, weight, tracking, leading, font } from '@/styles/tokens';

export interface PageShellProps {
  /** 主標題（必填） */
  title: ReactNode;
  /** 副標題說明 */
  description?: ReactNode;
  /** 標題右側的元數據（狀態 chip / ID / 時間等） */
  meta?: ReactNode;
  /** 右上角操作區（按鈕、Segmented 等） */
  actions?: ReactNode;
  /** 標題區下方的橫幅（如警告、提示） */
  banner?: ReactNode;
  /** 主內容 */
  children?: ReactNode;
  /** 是否將 page-header 作為 sticky（隨頁面滾動駐留） */
  sticky?: boolean;
  /** 內容區與 header 之間的間距，預設 24px */
  contentGap?: number;
  /** 容器最大寬度（可選，用於需要居中的頁面） */
  maxWidth?: number | string;
}

export default function PageShell({
  title,
  description,
  meta,
  actions,
  banner,
  children,
  sticky = false,
  contentGap = space[6],
  maxWidth,
}: PageShellProps) {
  return (
    <div
      style={{
        maxWidth,
        marginInline: maxWidth ? 'auto' : undefined,
      }}
    >
      <div
        className="zhcb-page-header"
        style={{
          marginBottom: contentGap,
          paddingBottom: space[5],
          /* 精緻的底部 1px 分割線，比卡片式 header 更輕盈 */
          borderBottom: `1px solid ${color.border.subtle}`,
          ...(sticky
            ? {
                position: 'sticky',
                top: 0,
                zIndex: 5,
                background: color.surface.canvas,
                paddingBlock: space[4],
                /* sticky 時加底部陰影強化層次感 */
                boxShadow: '0 1px 0 var(--border-subtle)',
              }
            : null),
        }}
      >
        {/* 標題行：eyebrow + title + description  |  actions */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: space[4],
            flexWrap: 'wrap',
          }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            {/* 主標題：Onest display，weight 600 */}
            <h1
              style={{
                margin: 0,
                fontFamily: font.display,
                fontSize: text['2xl'],
                fontWeight: weight.semibold,
                color: color.text.primary,
                letterSpacing: tracking.tight,
                lineHeight: leading.tight,
              }}
            >
              {title}
            </h1>
            {description && (
              <p
                style={{
                  margin: 0,
                  marginTop: 6,
                  fontSize: text.md,
                  color: color.text.tertiary,
                  lineHeight: leading.snug,
                  maxWidth: 680,
                }}
              >
                {description}
              </p>
            )}
            {/* meta 行：狀態 / ID / 時間等 */}
            {meta && (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: space[3],
                  marginTop: space[3],
                  fontSize: text.sm,
                  color: color.text.tertiary,
                }}
              >
                {meta}
              </div>
            )}
          </div>

          {/* actions 右側 */}
          {actions && (
            <div
              style={{
                display: 'flex',
                gap: space[2],
                flexShrink: 0,
                alignItems: 'center',
              }}
            >
              {actions}
            </div>
          )}
        </div>

        {/* banner（可選，警告/提示） */}
        {banner && <div style={{ marginTop: space[4] }}>{banner}</div>}
      </div>

      {children}
    </div>
  );
}

/* ────────────────────────────────────────────────
 * PageMetaItem — 用於 PageShell.meta 的並排元數據片段
 * ──────────────────────────────────────────────── */

export interface PageMetaItemProps {
  /** 標籤（小寫弱化） */
  label: ReactNode;
  /** 值（可以是 ID、時間、狀態等） */
  value: ReactNode;
  /** 圖標（可選，置於最前） */
  icon?: ReactNode;
  /** 是否使用 mono 字體（編號類） */
  mono?: boolean;
}

export function PageMetaItem({ label, value, icon, mono }: PageMetaItemProps) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: space[2],
        fontSize: text.sm,
        color: color.text.tertiary,
      }}
    >
      {icon && (
        <span style={{ display: 'flex', color: color.text.muted, flexShrink: 0 }}>{icon}</span>
      )}
      <span style={{ color: color.text.muted }}>{label}：</span>
      <span
        style={{
          color: color.text.primary,
          fontWeight: weight.medium,
          fontFamily: mono ? font.mono : font.body,
          /* tabular numerals 確保對齊 */
          fontVariantNumeric: mono ? 'tabular-nums' : undefined,
        }}
      >
        {value}
      </span>
    </span>
  );
}

/* ────────────────────────────────────────────────
 * PageMetaSeparator — meta 行間的分隔點
 * ──────────────────────────────────────────────── */
export function PageMetaSeparator() {
  return (
    <span
      aria-hidden
      style={{
        width: 3,
        height: 3,
        borderRadius: '50%',
        background: color.text.disabled,
        display: 'inline-block',
        flexShrink: 0,
      }}
    />
  );
}
